import { Button } from '~/components/ui/button';
import { checkoutReviewCopy, reconcileView, type CheckoutSide } from './pro-builder-checkout-choice';
import { useProBuilderPublishStore } from './pro-builder-publish-store';
import type { GitNeedsAction } from './publish-stream';

type GitVersion = NonNullable<GitNeedsAction['versions']>[number];

export const RECOVERY_COPIES_TESTID = 'pro-builder-recovery-copies';

/** Opens Save App on this folder with its local recovery copies expanded and in view. */
export function revealRecoveryCopies(folder: string): void {
  useProBuilderPublishStore.getState().openRecoveryCopies(folder);
  requestAnimationFrame(() => document.querySelector(`[data-testid="${RECOVERY_COPIES_TESTID}"]`)?.scrollIntoView({ block: 'nearest' }));
}

/**
 * FRONT-3411: the reconcile buttons of a Git review that is not a checkout review
 * (a history cleanup, or an overlap found while saving or updating), with what
 * each choice does when the review has its own words for it.
 */
export function ReconcileChoice({ action, busy, onChoose, autofocus = false }: { action: GitNeedsAction; busy: boolean; onChoose: (choice: CheckoutSide) => unknown; autofocus?: boolean }) {
  const view = reconcileView(action);
  if (!view.options.length) return null;
  return (
    <div data-testid="pro-builder-reconcile-choice" className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {view.options.map((option) => (
          <Button key={option.choice} type="button" size="sm" variant={option.primary ? 'default' : 'outline'} disabled={busy} data-choice={option.choice} {...(autofocus && option.primary ? { 'data-pro-builder-autofocus': '' } : {})} onClick={() => onChoose(option.choice)}>
            {option.label}
          </Button>
        ))}
      </div>
      {view.options.some((option) => option.detail) && (
        <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
          {view.options.map((option) => option.detail && <li key={option.choice}><span className="font-medium text-foreground">{option.label}:</span> {option.detail}</li>)}
        </ul>
      )}
    </div>
  );
}

/**
 * FRONT-3477: a checkout review (resume the work in this terminal, or start from
 * the app), worded the same on the terminal screen and in Save App. Renders
 * nothing for any other review.
 */
export function CheckoutReview({ action, busy, onChoose, onRecoveryCopies, titleId, autofocus = false }: {
  action: GitNeedsAction;
  busy: boolean;
  onChoose: (choice: CheckoutSide) => unknown;
  onRecoveryCopies: () => void;
  titleId?: string;
  autofocus?: boolean;
}) {
  const copy = checkoutReviewCopy(action);
  if (!copy) return null;
  // Without a side the content proves, nothing is preselected: the heading takes the keyboard instead.
  const headingFocus = autofocus && !copy.options.some((option) => option.primary);
  return (
    <div data-testid="pro-builder-checkout-review" className="flex flex-col gap-3 text-sm">
      <p id={titleId} className="text-base font-medium text-foreground outline-none" {...(headingFocus ? { tabIndex: -1, 'data-pro-builder-autofocus': '' } : {})}>{copy.title}</p>
      <p data-testid="pro-builder-checkout-sentence" className="text-muted-foreground">
        {copy.sentence.before}{copy.sentence.time && <strong className="font-medium text-foreground">{copy.sentence.time}</strong>}{copy.sentence.after}
      </p>
      <div className="flex flex-wrap gap-2">
        {copy.options.map((option) => (
          <Button key={option.choice} type="button" variant={option.primary ? 'default' : 'outline'} disabled={busy} data-choice={option.choice} {...(autofocus && option.primary ? { 'data-pro-builder-autofocus': '' } : {})} onClick={() => onChoose(option.choice)}>
            {option.label}
          </Button>
        ))}
      </div>
      <p data-testid="pro-builder-checkout-note" className="text-xs text-muted-foreground">
        {copy.note.lead && <strong className="font-medium text-foreground">{copy.note.lead}</strong>}{copy.note.text}{' '}
        <Button type="button" variant="link" size="xs" className="h-auto p-0 text-xs" data-testid="pro-builder-recovery-link" onClick={onRecoveryCopies}>Find it in your recovery copies</Button>
      </p>
      {action.paths.length > 0 && (
        <details data-testid="pro-builder-checkout-changes" className="rounded-md border p-3">
          <summary className="cursor-pointer text-xs font-medium text-foreground">{copy.filesLabel}</summary>
          <ChangedFiles action={action} labels={copy.fileLabels} />
        </details>
      )}
    </div>
  );
}

/** Each differing file, with its two versions behind "Show the changes" when the review carries them. */
function ChangedFiles({ action, labels }: { action: GitNeedsAction; labels: Record<string, string> | null }) {
  const versions = new Map(action.versions?.map((version) => [version.path, version]));
  return (
    <ul className="mt-3 flex flex-col gap-2">
      {action.paths.map((path) => {
        const version = versions.get(path);
        const name = <span className="break-all font-mono text-xs">{path}{labels?.[path] && <span data-testid="pro-builder-file-verdict" className="font-sans text-muted-foreground"> · {labels[path]}</span>}</span>;
        return <li key={path}>
          {version ? <details className="group">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
              {name}
              <span className="shrink-0 text-xs text-primary"><span className="group-open:hidden">Show the changes</span><span className="hidden group-open:inline">Hide the changes</span></span>
            </summary>
            <div className="mt-2"><GitVersionView version={version} /></div>
          </details> : name}
        </li>;
      })}
    </ul>
  );
}

function GitVersionView({ version }: { version: GitVersion }) {
  return <>
    {version.binary ? <p>Binary file — content is not shown.</p> : <div className="grid grid-cols-2 gap-2">{(['local', 'current'] as const).map((kind) => <div key={kind}><p>{kind === 'local' ? 'My local version' : 'Current app version'}</p><pre className="max-h-48 overflow-auto whitespace-pre-wrap text-xs">{version[kind] === null ? 'Missing or deleted' : version[kind]}</pre></div>)}</div>}
    {version.truncated && <p>Preview truncated: only the start of each version is shown.</p>}
  </>;
}

/** The files a review names, each with both versions when they can be shown. */
export function GitVersionsList({ action }: { action: GitNeedsAction }) {
  return <>
    <ul>{action.paths.map((path) => <li key={path} className="break-all font-mono text-xs">{path}</li>)}</ul>
    {action.versions?.map((version) => <details key={version.path}><summary className="break-all">Review versions: {version.path}</summary>
      <GitVersionView version={version} />
    </details>)}
  </>;
}

export function GitActionDetails({ action }: { action: GitNeedsAction }) {
  return <div role="alert" className="flex flex-col gap-2">
    <p>{action.message}</p>
    <GitVersionsList action={action} />
  </div>;
}
